import { useContext, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { UserContext } from "../../../../hooks/UserContext";
import { API } from "../../../../api";
import { Job, type Branch, type Deal, type ProagricaTypes } from "../../../../models";
import { Oops } from "../../../../utilities/oops";
import { Proagrica } from "../../../../utilities/integrations";
import { FileOptionTypes, GeoJSONOption } from "../../../../utilities/types";
import type { FeatureStats } from "../../ImportTable/ImportTable/ImportTable.types";
import { generateFileNames, ImportFormProps } from "../../shared/ImportHelpers";
import { ImportFormProagricaViewModel } from "./ProagricaImportForm.types";

export function useImportFormProagricaViewModel(props: ImportFormProps): ImportFormProagricaViewModel {
	// Context
	const userContext = useContext(UserContext);

	// State
	const [growers, setGrowers] = useState<ProagricaTypes.Grower[]>([]);
	const [selectedGrowers, setSelectedGrowers] = useState<ProagricaTypes.Grower[]>([]);
	const [farms, setFarms] = useState<ProagricaTypes.GrowerFarm[]>([]);
	const [fields, setFields] = useState<ProagricaTypes.Field[]>([]);
	const [selectedFields, setSelectedFields] = useState<ProagricaTypes.Field[]>([]);
	const [samplingItems, setSamplingItems] = useState<any[]>([]);
	const [jobs, setJobs] = useState<Job[]>([]);
	const [featureStats, setFeatureStats] = useState<FeatureStats | undefined>(undefined);
	const [loading, setLoading] = useState<string | boolean>(false);

	// Internal Branch/Deal selection state
	const [internalSelectedBranch, setInternalSelectedBranch] = useState<Branch | null>(
		props.selectedBranches?.[0] ?? null,
	);
	const [internalSelectedDeal, setInternalSelectedDeal] = useState<Deal | null>(props.selectedDeals?.[0] ?? null);

	// Computed values
	const canShowLoginButton = useMemo(() => !userContext?.proagricaAuth, [userContext?.proagricaAuth]);

	const canShowBranchSelector = useMemo(() => !!props.branches && props.branches.length > 0, [props.branches]);

	const canLoadGrowers = useMemo(
		() => !!userContext?.proagricaAuth && !!internalSelectedDeal,
		[userContext?.proagricaAuth, internalSelectedDeal],
	);

	const showGrowerSelector = useMemo(() => growers.length > 0, [growers.length]);

	const canLoadFields = useMemo(() => selectedGrowers.length > 0, [selectedGrowers.length]);

	const showFieldSelector = useMemo(() => fields.length > 0, [fields.length]);

	const canLoadJobs = useMemo(() => selectedFields.length > 0, [selectedFields.length]);

	const showLoadingSpinner = useMemo(() => loading === "submit", [loading]);

	const showImportTable = useMemo(() => jobs.length > 0 && !props.isInModal, [jobs.length, props.isInModal]);

	// Reset functions (internal, cascading)
	function resetSamplingEvents() {
		setJobs([]);
		setFeatureStats(undefined);
	}

	function resetFields() {
		setFields([]);
		setSelectedFields([]);
		resetSamplingEvents();
	}

	function resetFarms() {
		setFarms([]);
		setSamplingItems([]);
		resetFields();
	}

	function resetGrowers() {
		setGrowers([]);
		setSelectedGrowers([]);
		resetFarms();
	}

	// Event handlers
	const handleGrowerChange = useCallback((_event: any, growers: ProagricaTypes.Grower[]) => {
		setFields([]);
		setSelectedFields([]);
		setJobs([]);
		setSamplingItems([]);
		setSelectedGrowers(growers);
	}, []);

	const resetForm = useCallback(() => {
		resetGrowers();
	}, []);

	// Internal Branch/Deal handlers
	const handleInternalBranchChange = useCallback((branch: Branch | null) => {
		setInternalSelectedBranch(branch);
		setInternalSelectedDeal(null);
		resetGrowers();
	}, []);

	const handleInternalDealChange = useCallback((deal: Deal | null) => {
		setInternalSelectedDeal(deal);
		resetGrowers();
	}, []);

	// API calls
	const login = useCallback(async () => {
		try {
			const authUrl = await API.Integrations.Proagrica.login(window.location.href);
			setLoading(true);
			window.location.href = authUrl;
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.ProagricaImportForm.login");
		}
	}, []);

	const getGrowers = useCallback(async () => {
		setLoading(true);
		await API.Integrations.Proagrica.refreshToken();
		const loadingGrowersToast = toast.loading("Loading Growers...");
		try {
			setFields([]);
			setSelectedFields([]);
			setJobs([]);
			setSamplingItems([]);
			setSelectedGrowers([]);

			const growers = await API.Integrations.Proagrica.getGrowers();
			setGrowers(growers);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.ProagricaImportForm.getGrowers");
		} finally {
			setLoading(false);
			if (loadingGrowersToast && toast.isActive(loadingGrowersToast)) toast.dismiss(loadingGrowersToast);
		}
	}, []);

	const getFields = useCallback(async () => {
		setLoading(true);
		setFields([]);
		setSelectedFields([]);
		setJobs([]);
		await API.Integrations.Proagrica.refreshToken();

		try {
			// Phase 1: Collect all farms for all growers
			const loadingFarmsToast = toast.loading("Loading Farms...");
			const farmsWithGrowers = await API.Integrations.Proagrica.getFarmsForGrowers(
				selectedGrowers.map((g) => g.ID),
				selectedGrowers[0].SyncID,
			);
			setFarms(farmsWithGrowers.flatMap(({ farms }) => farms));
			if (loadingFarmsToast && toast.isActive(loadingFarmsToast)) toast.dismiss(loadingFarmsToast);

			// Phase 2: Process farms to get fields
			const total = selectedGrowers.length;
			let loaded = 0;
			const allFields: ProagricaTypes.Field[] = [];
			const loadingFieldsToast = toast.loading("Loading Fields...", { progress: 0 });

			for (const { farms, grower: growerId } of farmsWithGrowers) {
				const grower = selectedGrowers.find((g) => g.ID === growerId);
				if (!grower) continue;

				if (loadingFieldsToast)
					toast.update(loadingFieldsToast, {
						render: `Loading Fields for ${grower.Name}...`,
						progress: loaded / total,
					});

				const fields = await API.Integrations.Proagrica.getFieldsForGrowerFarms(grower, farms, grower.SyncID);
				allFields.push(...fields);
				loaded++;
			}

			if (loadingFieldsToast && toast.isActive(loadingFieldsToast)) toast.dismiss(loadingFieldsToast);
			setFields(allFields);
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.ProagricaImportForm.getFields");
		} finally {
			setLoading(false);
		}
	}, [selectedGrowers]);

	const getSamplingEvents = useCallback(async () => {
		setLoading(true);
		const loadingEvents = toast.loading("Loading Sampling Events...", { progress: 0 });

		if (!internalSelectedDeal) {
			toast.error("No deal selected");
			setLoading(false);
			if (loadingEvents && toast.isActive(loadingEvents)) toast.dismiss(loadingEvents);
			return;
		}
		const _growerDealMap: Record<string, Deal> = {};
		const _growerBranchMap: Record<string, Branch> = {};
		for (const grower of selectedGrowers) {
			_growerDealMap[grower.ID] = internalSelectedDeal;
			if (internalSelectedBranch) {
				_growerBranchMap[grower.ID] = internalSelectedBranch;
			}
		}

		try {
			setJobs([]);
			setFeatureStats(undefined);
			await API.Integrations.Proagrica.refreshToken();

			const newJobs: Job[] = [];
			const total = fields.length;
			let loaded = 0;
			for (const grower of selectedGrowers) {
				const deal = _growerDealMap[grower.ID];
				const growerFarms = farms.filter((farm) => farm.ParentID === grower.ID);

				for (const growerFarm of growerFarms) {
					const farm = await API.Integrations.Proagrica.getFarm(growerFarm.ID, grower.SyncID);
					const growerFields = fields.filter((field) => field.FarmID === growerFarm.ID);

					for (const field of growerFields) {
						if (!selectedFields.includes(field)) {
							continue;
						}
						if (loadingEvents)
							toast.update(loadingEvents, {
								render: `Loading Sampling Events for ${grower.Name} - ${field.Name ?? ""}...`,
								progress: loaded / total,
							});

						const s = await API.Integrations.Proagrica.getSamplingItems(field.ID, field.SyncID);
						samplingItems.push(...s);

						const fieldSamplingItems = samplingItems
							.filter((item) => item.FieldID === field.ID)
							.sort((a, b) => {
								return (b["ModifiedOn"] as string).localeCompare(a["ModifiedOn"]);
							});

						const { bndName, ptsName, znsName } = generateFileNames(grower.Name, farm.Name, field.Name);
						const bndGeoJSON = Proagrica.getBoundaryGeoJSON(field.CurrentBoundary.Records);
						const bndOption: GeoJSONOption = { name: bndName, geoJSON: bndGeoJSON };

						let znsGeoJSON: GeoJSON.FeatureCollection | undefined;
						let ptsGeoJSON: GeoJSON.FeatureCollection | undefined;
						let znsOptions: GeoJSONOption[] = [];
						let ptsOptions: GeoJSONOption[] = [];
						let isZoneJob = false;
						if (
							fieldSamplingItems &&
							fieldSamplingItems.length > 0 &&
							fieldSamplingItems[0].SoilSamplingRecords &&
							fieldSamplingItems[0].SoilSamplingRecords.length > 0
						) {
							isZoneJob = fieldSamplingItems[0].SoilSamplingRecords[0].WKT.toLowerCase().includes("polygon");
							const geoJSON = await Proagrica.getZoneOrPointsGeoJSON(fieldSamplingItems[0]);
							const option: GeoJSONOption = { name: isZoneJob ? znsName : ptsName, geoJSON: geoJSON };
							if (isZoneJob) {
								znsGeoJSON = geoJSON;
								znsOptions = [option];
							} else {
								ptsGeoJSON = geoJSON;
								ptsOptions = [option];
							}
						}

						const newJob: Job = Job.defaultForImport(
							field.Name,
							deal.depth,
							deal.density,
							grower.Name,
							deal.frequencyOfMicros,
							deal.testPackage,
							farm.Name,
						);

						// Store GeoJSON directly - no SHP files needed!
						newJob.files = {
							pointsGeoJSON: ptsGeoJSON,
							boundaryGeoJSON: bndGeoJSON,
							zonesGeoJSON: znsGeoJSON,
						};

						newJob.fileOptions = {
							boundaryOptions: [{ type: FileOptionTypes.Boundary, options: [bndOption] }],
							boundaryGeoJSON: bndGeoJSON,
							pointsOptions: [
								{ type: FileOptionTypes.Centroids, options: isZoneJob ? [] : ptsOptions },
								{ type: FileOptionTypes.Cores, options: isZoneJob ? ptsOptions : [] },
							],
							pointsGeoJSON: ptsGeoJSON,
							zonesOptions: [{ type: FileOptionTypes.Zones, options: znsOptions }],
							zonesGeoJSON: znsGeoJSON,
						};
						newJob.dealIds = deal.id ? [deal.id] : [];
						newJob.fieldId = field.ID;
						newJob.fieldPriority = "Regular Turn (default)";
						newJob.submitterEmail = props.user?.email;
						newJob.submitterName = props.user?.name;
						newJob.branchIds = internalSelectedBranch ? [internalSelectedBranch.id] : [];
						newJob.externalGrower = {
							id: grower.ID,
							name: grower.Name,
						};

						newJob.eventId = uuidv4(); // not sure why we're doing this... - DRB

						loaded++;
						if (loadingEvents)
							toast.update(loadingEvents, {
								render: `Loading Sampling Events for ${grower.Name} - ${field.Name ?? ""}...`,
								progress: loaded / total,
							});
						newJobs.push(newJob);
					}
				}
			}

			const stats = {
				zones: newJobs.reduce((sum, job) => sum + (job.files.zonesGeoJSON?.features?.length || 0), 0),
				centroids: newJobs.reduce(
					(sum, job) =>
						sum + (job.files.zonesGeoJSON?.features?.length ? 0 : job.files.pointsGeoJSON?.features?.length || 0),
					0,
				),
				cores: newJobs.reduce(
					(sum, job) =>
						sum + (job.files.zonesGeoJSON?.features?.length ? job.files.pointsGeoJSON?.features?.length || 0 : 0),
					0,
				),
			};
			setFeatureStats(stats);

			setJobs(newJobs);

			// If in modal mode, notify parent and let it handle the ImportTable
			if (props.onJobsExtracted) {
				props.onJobsExtracted(newJobs, stats);
			}
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.ProagricaImportForm.getSamplingEvents");
		} finally {
			setLoading(false);
			if (loadingEvents && toast.isActive(loadingEvents)) toast.dismiss(loadingEvents);
		}
	}, [props, selectedGrowers, selectedFields, farms, samplingItems, internalSelectedDeal, internalSelectedBranch]);

	return {
		// State
		growers,
		selectedGrowers,
		farms,
		fields,
		selectedFields,
		jobs,
		featureStats,
		loading,

		// Internal Branch/Deal selection
		internalSelectedBranch,
		internalSelectedDeal,
		canShowBranchSelector,
		handleInternalBranchChange,
		handleInternalDealChange,

		// Computed values
		canShowLoginButton,
		canLoadGrowers,
		showGrowerSelector,
		canLoadFields,
		showFieldSelector,
		canLoadJobs,
		showLoadingSpinner,
		showImportTable,

		// Context
		userContext,

		// State setters
		setSelectedGrowers,
		setSelectedFields,

		// Event handlers
		handleGrowerChange,

		// API calls
		login,
		getGrowers,
		getFields,
		getSamplingEvents,

		// Reset
		resetForm,
	};
}
